<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5" cols="12">
                <h1 class="text-left text-h5 font-weight-bold">Invoices</h1>
                <p class="text-body-2 text-medium-emphasis text-left mb-0">View and manage your billing history</p>
            </v-col>
        </v-row>

        <v-row class="mt-1">
            <v-col cols="12" md="3" sm="4">
                <v-select v-model="formData['year']" :items="years" clearable density="compact" label="Year"
                          variant="outlined" rounded="lg"
                          @update:model-value="fetchInvoices"/>
            </v-col>
            <v-col cols="12" md="3" sm="4">
                <v-select v-model="formData['month']" :items="months" clearable density="compact" label="Month"
                          variant="outlined" rounded="lg"
                          @update:model-value="fetchInvoices"/>
            </v-col>
            <v-col cols="12" md="3" sm="4">
                <v-select v-model="formData['status']" :items="statuses" clearable density="compact"
                          label="Status" variant="outlined" rounded="lg"
                          @update:model-value="fetchInvoices"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card variant="outlined" rounded="lg">
                    <v-data-table v-model:expanded="expanded" :headers="tableHeaders" :items="tableItems"
                                  :loading="loading"
                                  density="compact"
                                  hide-default-footer
                                  show-expand
                    >
                        <template v-slot:loading>
                            <v-skeleton-loader type="table-row@7"></v-skeleton-loader>
                        </template>
                        <template v-slot:[`item.id`]="{ item }">
                            <td class="font-weight-medium">{{ `NEXTERA-${item.id}` }}</td>
                        </template>
                        <template v-slot:[`item.status`]="{ item }">
                            <td>
                                <v-chip size="small" variant="tonal"
                                        :color="item.status === 1 ? 'success' : item.status === 2 ? 'error' : 'warning'">
                                    {{ status(item.status) }}
                                </v-chip>
                            </td>
                        </template>
                        <template v-slot:[`item.action`]="{ item }">
                            <td>
                                <v-btn v-if="item.status === 0" :loading="btnLoading" color="success" size="small"
                                       variant="tonal" prepend-icon="mdi-credit-card"
                                       @click="payInvoice(item.id)">Pay
                                </v-btn>
                            </td>
                        </template>
                        <template v-slot:expanded-row="{ columns, item }">
                            <tr>
                                <td :colspan="columns.length" class="pa-4 bg-grey-lighten-5">
                                    <v-table density="compact" class="bg-transparent">
                                        <thead>
                                        <tr>
                                            <th>Plan</th>
                                            <th>Qty</th>
                                            <th>Unit Price</th>
                                            <th>Discount</th>
                                            <th>Sub Total</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="itemDetail in item.items" :key="itemDetail.plan_name">
                                            <td>{{ itemDetail.plan_name }}</td>
                                            <td>{{ itemDetail.quantity }}</td>
                                            <td>{{ `${itemDetail.unit_price} ${item.currency}` }}</td>
                                            <td>{{
                                                    itemDetail.discount_price ? `${itemDetail.discount_price} ${item.currency}` : '-'
                                                }}
                                            </td>
                                            <td class="font-weight-medium">{{
                                                    `${parseInt(itemDetail.quantity) * parseFloat(itemDetail.discount_price ?? itemDetail.unit_price)} ${item.currency}`
                                                }}
                                            </td>
                                        </tr>
                                        </tbody>
                                    </v-table>
                                </td>
                            </tr>
                        </template>
                        <template v-slot:no-data>
                            <div class="text-center pa-8">
                                <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-receipt-text-outline</v-icon>
                                <p class="text-body-1 text-medium-emphasis mb-1">No invoices found</p>
                                <p class="text-body-2 text-medium-emphasis mb-0">
                                    Your billing history will appear here
                                </p>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination v-model="currentPage" :length="totalPages" class="my-4" density="compact"
                              @update:model-value="fetchInvoices"></v-pagination>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {send} from "@/composables/apiClient";
import {computed, onMounted, reactive, ref} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";

const expanded = ref([]);
const tableHeaders = [
    {
        title: 'ID', align: 'start', sortable: false, key: 'id',
    },
    {
        title: 'Total Price', align: 'start', sortable: false, key: 'total_price',
    },
    {
        title: 'Card Brand', align: 'start', sortable: false, key: 'brand',
    },
    {
        title: 'Card last 4', align: 'start', sortable: false, key: 'last_four',
    },
    {
        title: 'Invoice Date', align: 'start', sortable: false, key: 'invoice_date',
    },
    {
        title: 'Status', align: 'start', sortable: false, key: 'status',
    },
    {
        title: 'Action', align: 'start', sortable: false, key: 'action',
    },
];
const tableItems = computed(() => {
    if (invoices.value.length) {
        return invoices.value.map((item, index) => {
            return {
                id: item.id,
                currency: item.currency,
                total_price: `${item.total_price} ${item.currency}`,
                brand: item.brand?.toUpperCase(),
                last_four: `${item.last_four ? '**** ' + item.last_four : ''}`,
                invoice_date: item.invoice_date,
                status: item.status,
                items: item.items
            }
        })
    } else {
        return []
    }
})
const invoices = ref([]);
const totalPages = ref(1);
const currentPage = ref(1);
const loading = ref(false);
const btnLoading = ref(false);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    year: null,
    month: null,
    status: null,
});
const statuses = [{value: 0, title: 'Pending'}, {value: 1, title: 'Paid'}, {value: 2, title: 'Cancelled'}];
const months = [
    {value: 1, title: 'January'},
    {value: 2, title: 'February'},
    {value: 3, title: 'March'},
    {value: 4, title: 'April'},
    {value: 5, title: 'May'},
    {value: 6, title: 'June'},
    {value: 7, title: 'July'},
    {value: 8, title: 'August'},
    {value: 9, title: 'September'},
    {value: 10, title: 'October'},
    {value: 11, title: 'November'},
    {value: 12, title: 'December'},
];
const years = [];
for (let i = 2024; i <= (new Date()).getFullYear(); i++) {
    years.push(i.toString());
}
onMounted(() => {
    fetchInvoices();
});
const status = (id) => {
    return statuses.find((status) => status.value === id)['title'];
}
const fetchInvoices = () => {
    loading.value = true;
    const params = {page: currentPage.value};
    if (formData.year) params.year = formData.year;
    if (formData.month) params.month = formData.month;
    if (formData.status !== null && formData.status !== undefined) params.status = formData.status;
    send('get', `invoices`, params)
        .then((response) => {
            invoices.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((errorResponse) => {
            if (errorResponse.response.data.message) {
                showSnackbar(errorResponse.response.data.message, 'error');
            } else {
                showSnackbar('Something went wrong', 'error');
            }
        })
        .finally(() => {
            loading.value = false
        });
}
const payInvoice = (invoiceID) => {
    if (confirm("Are you sure to pay invoice with your default payment method?")) {
        btnLoading.value = true;
        send('post', `invoices/pay/${invoiceID}`)
            .then((response) => {
                if (response.data.success) {
                    invoices.value.map((invoice, index) => {
                        if (invoice.id === invoiceID) {
                            invoices.value[index]['status'] = response.data.data.status;
                            invoices.value[index]['brand'] = response.data.data.brand;
                            invoices.value[index]['last_four'] = response.data.data.last_four;
                            invoices.value[index]['payment_datetime'] = response.data.data.payment_datetime;
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((errorResponse) => {
                if (errorResponse.response.data.message) {
                    showSnackbar(errorResponse.response.data.message, 'error');
                } else {
                    showSnackbar('Something went wrong', 'error');
                }
            })
            .finally(() => {
                btnLoading.value = false
            });
    }
}
</script>

<style>
td.v-data-table__td.v-data-table-column--align-start {
    text-align: left;
}
</style>
